import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Printer, Loader2, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { api, reportExportUrl } from "../lib/api";
import { STATUS_LABELS } from "../lib/constants";

const statusColor = (s) => (s === "expired" ? "#b91c1c" : s === "expiring_soon" ? "#b45309" : "#047857");
const remaining = (d) => (d < 0 ? `منتهية منذ ${Math.abs(d)} يوم` : d === 0 ? "اليوم" : `${d} يوم`);

export const ReportDialog = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.report(30));
    } catch {
      toast.error("تعذّر إنشاء التقرير");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handlePrint = () => {
    const el = document.getElementById("report-print-area");
    if (!el) return;
    const w = window.open("", "_blank");
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>تقرير الوثائق</title>
      <style>
        body{font-family:SaudiRiyal,'Tajawal','IBM Plex Sans Arabic',sans-serif;padding:30px;color:#0f172a}
        h1{font-size:22px;margin:0 0 4px} .sub{color:#64748b;font-size:13px;margin-bottom:20px}
        h2{font-size:16px;margin:22px 0 8px;border-bottom:2px solid #059669;padding-bottom:4px;color:#065f46}
        table{width:100%;border-collapse:collapse;margin-bottom:10px;font-size:13px}
        th,td{border:1px solid #e2e8f0;padding:7px 10px;text-align:right}
        th{background:#f1f5f9}
        .badge{font-weight:700}
      </style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const branches = data?.branches || {};
  const branchKeys = Object.keys(branches);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl font-body max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">تقرير الوثائق المنتهية والقريبة من الانتهاء</DialogTitle>
          <DialogDescription className="text-right">الوثائق المنتهية أو التي تنتهي خلال 30 يومًا، مجمّعة حسب الفرع.</DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2 mb-2">
          <a href={reportExportUrl(30)} data-testid="export-excel-link"
            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white transition ${(!data || data.total === 0) ? "bg-blue-300 pointer-events-none" : "bg-blue-600 hover:bg-blue-700"}`}>
            <FileSpreadsheet className="h-4 w-4" /> تصدير Excel
          </a>
          <Button data-testid="print-report-button" onClick={handlePrint} disabled={!data || data.total === 0}
            className="bg-slate-900 hover:bg-slate-800 gap-2">
            <Printer className="h-4 w-4" /> طباعة / حفظ PDF
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <div id="report-print-area" data-testid="report-content">
            <h1 className="font-heading text-xl font-extrabold text-slate-900">تقرير الوثائق</h1>
            <div className="sub text-slate-500 text-sm mb-4">
              تاريخ الإنشاء: {data ? new Date(data.generated_at).toLocaleDateString("ar-EG") : ""} · إجمالي الوثائق: {data?.total ?? 0}
            </div>
            {(!data || data.total === 0) ? (
              <div className="py-12 text-center text-slate-400">
                <ShieldCheck className="h-10 w-10 mx-auto text-blue-400" />
                <p className="mt-2">لا توجد وثائق منتهية أو قريبة من الانتهاء. كل شيء على ما يرام!</p>
              </div>
            ) : (
              branchKeys.map((b) => (
                <div key={b} data-testid={`report-branch-${b}`}>
                  <h2 className="font-heading text-base font-bold text-blue-700 border-b-2 border-blue-600 pb-1 mt-5 mb-2">الفرع: {b} ({branches[b].length})</h2>
                  <table className="w-full border-collapse mb-3 text-sm">
                    <thead>
                      <tr>
                        {["الموظف", "الرقم الوظيفي", "الوثيقة", "تاريخ الانتهاء", "المتبقّي", "الحالة"].map((h) => (
                          <th key={h} className="border border-slate-200 bg-slate-100 px-2.5 py-1.5 text-right font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {branches[b].map((r, i) => (
                        <tr key={i}>
                          <td className="border border-slate-200 px-2.5 py-1.5 text-right">{r.employee}</td>
                          <td className="border border-slate-200 px-2.5 py-1.5 text-right">{r.employee_number || "—"}</td>
                          <td className="border border-slate-200 px-2.5 py-1.5 text-right">{r.document}</td>
                          <td className="border border-slate-200 px-2.5 py-1.5 text-right">{r.expiry_date}</td>
                          <td className="border border-slate-200 px-2.5 py-1.5 text-right">{remaining(r.days_left)}</td>
                          <td className="badge border border-slate-200 px-2.5 py-1.5 text-right font-bold" style={{ color: statusColor(r.status) }}>{STATUS_LABELS[r.status]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
